import { AlertCircle, Calendar, CheckCircle, Clock, CreditCard, Home, Mail, MapPin, Phone, Receipt, User } from 'lucide-react';
import { useEffect, useState } from 'react';
import { getSiteLink } from '../config/siteLinks';
import { useBooking } from '../contexts/BookingContext';
import { useTranslation } from '../hooks/useTranslation';
import '../styles/payment-info-light.css';
import { formatPriceWithEuro, formatWholeEurosWithEuro } from '../utils/priceFormatter';
import StepContainer from './StepContainer';
import StripePayment from './StripePayment';

const PaymentInfo = ({ onPaymentSubmit, bookingData }) => {
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const { markBookingAsCompleted } = useBooking();
  const { t, language } = useTranslation();

  useEffect(() => {
    if (!paymentSuccess) return;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [paymentSuccess]);

  const handlePaymentSuccess = (result) => {
    setPaymentSuccess(true);
    setPaymentError(null);
    setLastResult(result);
    markBookingAsCompleted();
    onPaymentSubmit({
      success: true,
      booking: result.booking,
      payment: result.payment
    });
  };

  const handlePaymentError = (error) => {
    setPaymentError(error.message);
    setPaymentSuccess(false);
  };

  if (paymentSuccess) {
    const booking = lastResult?.booking;
    const payment = lastResult?.payment;
    const totalFromServer = payment != null
      ? (payment.grossAmount ?? payment.amount)
      : null;
    const paidTotalServer =
      lastResult?.paidTotal != null && Number.isFinite(Number(lastResult.paidTotal))
        ? Number(lastResult.paidTotal)
        : totalFromServer != null
          ? Number(totalFromServer)
          : Number(bookingData.pricing?.grandTotal ?? 0);
    const balanceDueNum =
      lastResult?.balanceDue != null && Number.isFinite(Number(lastResult.balanceDue))
        ? Number(lastResult.balanceDue)
        : 0;
    const hasPendingBalance = balanceDueNum > 0.02;
    const formatEuroSmart = (n) =>
      Number.isFinite(n) && Math.abs(n - Math.round(n)) < 0.001
        ? formatWholeEurosWithEuro(Math.round(n))
        : formatPriceWithEuro(n);
    const paidDisplay = formatEuroSmart(paidTotalServer);
    const pendingDisplay = formatEuroSmart(balanceDueNum);
    const bookingTotalRaw = booking?.total != null ? Number(booking.total) : NaN;
    const fromPricing = Number(bookingData.pricing?.grandTotal ?? 0);
    const fromPaidAndBalance =
      Number.isFinite(paidTotalServer) && Number.isFinite(balanceDueNum)
        ? Math.round((paidTotalServer + balanceDueNum) * 100) / 100
        : NaN;
    const reservationGrandTotal = Number.isFinite(bookingTotalRaw)
      ? bookingTotalRaw
      : Number.isFinite(fromPricing) && fromPricing > 0
        ? fromPricing
        : Number.isFinite(fromPaidAndBalance)
          ? fromPaidAndBalance
          : 0;
    const reservationTotalLabel = formatEuroSmart(reservationGrandTotal);
    const displayData = {
      bookingId: booking?.id ?? bookingData.bookingId ?? '—',
      bookingReference: booking?.bookingReference ?? null,
      customerName: bookingData.customerInfo?.name || '—',
      customerEmail: bookingData.customerInfo?.email || '—',
      customerPhone: bookingData.customerInfo?.phone || '—',
      total: paidTotalServer,
      totalPaidLabel: paidDisplay,
      hasPendingBalance,
      pendingDisplay,
      reservationTotalLabel,
      emailFailed: lastResult?.emailSent === false && !!lastResult?.emailError,
      startDate: bookingData.dates?.start || new Date().toISOString().split('T')[0],
      endDate: bookingData.dates?.end || new Date(Date.now() + 86400000 * 3).toISOString().split('T')[0]
    };

    return (
      <StepContainer className="payment-info-light">
        <div className="max-w-4xl mx-auto">
          {/* Header con animación */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-24 h-24 sm:w-28 sm:h-28 bg-gradient-to-br from-[#EBC122] to-[#d4a91e] rounded-full mb-6 shadow-lg animate-bounce-subtle">
              <CheckCircle size={48} className="text-white" strokeWidth={3} />
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-2c3e50 mb-3 font-orbitron">
              {t('payment.confirmation.success.title')}
            </h2>
            <p className="text-lg sm:text-xl text-6c757d">
              {t('payment.confirmation.success.message')}
            </p>
          </div>

          {/* Card principal con información de la reserva */}
          <div className="card confirmation-card mb-6">
            <div className="card-header bg-gradient-to-r from-[#EBC122]/10 to-[#EBC122]/5 border-b-2 border-[#EBC122]/30">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#EBC122]/20 rounded-lg">
                  <Receipt size={24} className="text-[#EBC122]" />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-2c3e50 font-orbitron">
                  {t('payment.confirmation.booking.details')}
                </h3>
              </div>
            </div>
            <div className="card-content p-6 sm:p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Información de la reserva */}
                <div className="space-y-4">
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="p-2 bg-[#EBC122]/10 rounded-lg">
                      <Receipt size={20} className="text-[#EBC122]" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-6c757d mb-1">{t('payment.confirmation.booking.number')}</p>
                      <p className="text-base font-bold text-2c3e50">
                        {displayData.bookingReference ? `${displayData.bookingReference}` : `${displayData.bookingId}`}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="p-2 bg-[#EBC122]/10 rounded-lg">
                      <User size={20} className="text-[#EBC122]" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-6c757d mb-1">{t('payment.confirmation.booking.customer')}</p>
                      <p className="text-base font-semibold text-2c3e50">{displayData.customerName}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="p-2 bg-[#EBC122]/10 rounded-lg">
                      <Mail size={20} className="text-[#EBC122]" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-6c757d mb-1">{t('customer.email.label')}</p>
                      <p className="text-base font-semibold text-2c3e50">{displayData.customerEmail}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="p-2 bg-[#EBC122]/10 rounded-lg">
                      <Phone size={20} className="text-[#EBC122]" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-6c757d mb-1">{t('customer.phone.label')}</p>
                      <p className="text-base font-semibold text-2c3e50">{displayData.customerPhone}</p>
                    </div>
                  </div>
                </div>

                {/* Fechas y precio */}
                <div className="space-y-4">
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="p-2 bg-[#EBC122]/10 rounded-lg">
                      <Calendar size={20} className="text-[#EBC122]" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-6c757d mb-1">{t('booking.dates.label')}</p>
                      <p className="text-base font-semibold text-2c3e50">
                        {new Date(displayData.startDate).toLocaleDateString(language, { 
                          day: 'numeric', 
                          month: 'long', 
                          year: 'numeric' 
                        })}
                      </p>
                      <p className="text-sm text-6c757d mt-1">
                        {t('booking.dates.until')} {new Date(displayData.endDate).toLocaleDateString(language, { 
                          day: 'numeric', 
                          month: 'long', 
                          year: 'numeric' 
                        })}
                      </p>
                    </div>
                  </div>

                  {displayData.hasPendingBalance && (
                    <div className="p-4 rounded-lg border-2 border-[#EBC122]/25 bg-white/80">
                      <p className="text-xs text-6c757d mb-2">
                        {t('payment.confirmation.booking.total.reservation')}
                      </p>
                      <p className="text-2xl sm:text-3xl font-bold text-2c3e50">
                        {displayData.reservationTotalLabel}
                      </p>
                    </div>
                  )}

                  <div
                    className={`grid gap-4 ${
                      displayData.hasPendingBalance ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'
                    }`}
                  >
                    <div className="p-4 bg-gradient-to-br from-[#EBC122]/10 to-[#EBC122]/5 rounded-lg border-2 border-[#EBC122]/30">
                      <p className="text-xs text-6c757d mb-2">{t('payment.confirmation.booking.total.paid')}</p>
                      <p className="text-3xl sm:text-4xl font-bold text-2c3e50">
                        {displayData.totalPaidLabel}
                      </p>
                    </div>
                    {displayData.hasPendingBalance && (
                      <div className="p-4 rounded-lg border-2 border-amber-300 bg-amber-50">
                        <p className="text-xs text-amber-900/90 mb-2 font-medium">
                          {t('payment.confirmation.balance.pending')}
                        </p>
                        <p className="text-3xl sm:text-4xl font-bold text-amber-950">
                          {displayData.pendingDisplay}
                        </p>
                        <p className="text-sm text-amber-900/85 mt-3 leading-relaxed">
                          {t('payment.confirmation.balance.pending.note')}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {displayData.emailFailed && (
            <div className="mb-6 flex gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950">
              <AlertCircle className="flex-shrink-0 mt-0.5" size={20} />
              <span>{t('payment.confirmation.email.error')}</span>
            </div>
          )}

          {/* Información importante */}
          <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 border-2 border-blue-200 rounded-lg p-5 sm:p-6 mb-6">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-blue-500 rounded-lg flex-shrink-0">
                <MapPin size={24} className="text-white" />
              </div>
              <div className="flex-1">
                <h4 className="font-bold text-lg text-blue-900 mb-3 font-orbitron">
                  {t('payment.confirmation.next.steps.title')}
                </h4>
                <ul className="space-y-2 text-sm sm:text-base text-blue-800">
                  <li className="flex items-start gap-2">
                    <CheckCircle size={18} className="text-blue-600 mt-0.5 flex-shrink-0" />
                    <span>{t('payment.confirmation.next.steps.email')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={18} className="text-blue-600 mt-0.5 flex-shrink-0" />
                    <span>{t('payment.confirmation.next.steps.dni')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={18} className="text-blue-600 mt-0.5 flex-shrink-0" />
                    <span>{t('payment.confirmation.next.steps.arrive')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={18} className="text-blue-600 mt-0.5 flex-shrink-0" />
                    <span>{t('payment.confirmation.next.steps.contact')}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Mensaje final */}
          <div className="text-center p-6 bg-gradient-to-r from-[#EBC122]/5 to-[#EBC122]/10 rounded-lg border border-[#EBC122]/20">
            <p className="text-base sm:text-lg text-2c3e50 font-semibold">
              {t('payment.confirmation.thank.you')}
            </p>
            <p className="text-sm sm:text-base text-6c757d mt-2">
              {t('payment.confirmation.enjoy')}
            </p>
          </div>

          {/* Botón para ir a la home */}
          <div className="flex justify-center mt-6">
            <a
              href={getSiteLink('header.home', language)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#EBC122] text-primary-dark font-semibold rounded-lg hover:bg-[#d4a91e] transition-colors shadow-md"
            >
              <Home size={20} />
              {t('payment.confirmation.goHome')}
            </a>
          </div>
        </div>
      </StepContainer>
    );
  }

  return (
    <StepContainer className="payment-info-light">
      {/* <div className="stepper-header">
        <h2 className="stepper-title text-xl sm:text-2xl">Información de Pago</h2>
        <div className="stepper-progress">
          <CreditCard size={18} className="sm:w-5 sm:h-5" />
          <span className="text-sm sm:text-base">Paso 6 de 6</span>
        </div>
      </div> */}

      <StripePayment
        bookingData={bookingData}
        onPaymentSuccess={handlePaymentSuccess}
        onPaymentError={handlePaymentError}
      />
      
      {paymentError && (
        <div className="mt-4 sm:mt-6 alert alert-error">
          <div className="flex gap-2">
            <AlertCircle size={18} className="sm:w-5 sm:h-5" />
            <span className="text-sm sm:text-base">{paymentError}</span>
          </div>
        </div>
      )}
    </StepContainer>
  );
};

export default PaymentInfo; 